import {
  type StateStore,
  type User,
  UserManager,
  type UserManagerSettings,
  WebStorageStateStore,
} from "oidc-client-ts";
import { ApiError } from "./api";
import { requireBrowserOrigin } from "./browser";
import { isOidcInvalidClientError } from "./oidc-session/response";
import { withAppPublicUrlPath } from "./app-public-path";

export type OidcPrompt = "none" | "login";

export type StartOidcAuthorizationOptions = {
  prompt?: OidcPrompt;
};

export type OidcCallbackError = {
  error: string;
  errorDescription: string | null;
};

export type ResolveOidcClientId = (
  fetchImpl?: typeof fetch,
  forceRefresh?: boolean,
) => Promise<string>;

export type OidcBrowserClientConfig = {
  authority: string;
  scope: string;
  callbackPath: string;
  defaultRedirectPath: string;
  pendingRedirectPathKey: string;
  stateStorePrefix: string;
  userStorePrefix: string;
  resolveClientId: ResolveOidcClientId;
  sanitizeRedirectPath?: (rawValue: string | null | undefined, fallback?: string) => string;
};

export type CompleteOidcCallbackResult = {
  redirectPath: string;
  oidcUser: User;
  restorePendingRedirectPath: () => void;
};

function defaultSanitizeRedirectPath(rawValue: string | null | undefined, fallback = "/") {
  const value = String(rawValue || "").trim();
  if (!value || !value.startsWith("/") || value.startsWith("//")) {
    return fallback;
  }
  return value;
}

function isInvalidClientError(error: unknown) {
  if (error instanceof ApiError) {
    return isOidcInvalidClientError(error.detail);
  }
  return isOidcInvalidClientError(error);
}

function createOidcSecureContextRequiredError() {
  return new ApiError(0, {
    code: "platform.oidc.secure_context_required",
    message: "OIDC authorization requires a secure browser context for PKCE.",
  });
}

export function isOidcBrowserSecureContextAvailable() {
  if (typeof window === "undefined") {
    return true;
  }
  if (window.isSecureContext === true && window.crypto?.subtle) {
    return true;
  }
  return false;
}

function assertOidcBrowserSecureContext() {
  if (isOidcBrowserSecureContextAvailable()) {
    return;
  }
  throw createOidcSecureContextRequiredError();
}

export function readOidcCallbackError(
  currentUrl = typeof window !== "undefined" ? window.location.href : "",
): OidcCallbackError | null {
  if (!currentUrl) {
    return null;
  }
  const url =
    typeof window === "undefined"
      ? new URL(currentUrl)
      : new URL(
          currentUrl,
          requireBrowserOrigin("OIDC callback URL origin is only available in browser"),
        );
  const error = String(url.searchParams.get("error") || "").trim();
  if (!error) {
    return null;
  }
  const errorDescription = String(url.searchParams.get("error_description") || "").trim();
  return {
    error,
    errorDescription: errorDescription || null,
  };
}

export function createOidcBrowserClient(config: OidcBrowserClientConfig) {
  const sanitizeRedirectPath = config.sanitizeRedirectPath ?? defaultSanitizeRedirectPath;
  let userManagerPromise: Promise<UserManager> | null = null;
  const userStoreEntries = new Map<string, string>();

  const userStore: StateStore = {
    async get(key) {
      return userStoreEntries.get(key) ?? null;
    },
    async set(key, value) {
      userStoreEntries.set(key, value);
    },
    async remove(key) {
      const value = userStoreEntries.get(key) ?? null;
      userStoreEntries.delete(key);
      return value;
    },
    async getAllKeys() {
      return Array.from(userStoreEntries.keys());
    },
  };

  function reset() {
    userManagerPromise = null;
    userStoreEntries.clear();
  }

  function resolveStorage() {
    if (typeof window === "undefined") {
      return null;
    }
    return window.sessionStorage;
  }

  function getCallbackUrl() {
    if (typeof window === "undefined") {
      throw new Error("OIDC callback URL is only available in browser");
    }
    return new URL(withAppPublicUrlPath(config.callbackPath), window.location.origin).toString();
  }

  function getStoredPendingRedirectPath() {
    return resolveStorage()?.getItem(config.pendingRedirectPathKey) ?? null;
  }

  function setStoredPendingRedirectPath(redirectPath: string | null) {
    const storage = resolveStorage();
    if (!storage) {
      return;
    }
    if (!redirectPath) {
      storage.removeItem(config.pendingRedirectPathKey);
      return;
    }
    storage.setItem(config.pendingRedirectPathKey, redirectPath);
  }

  async function getUserManager(fetchImpl: typeof fetch = fetch, forceRefresh = false) {
    if (!forceRefresh && userManagerPromise) {
      return await userManagerPromise;
    }
    userManagerPromise = (async () => {
      const clientId = await config.resolveClientId(fetchImpl, forceRefresh);
      const storage = resolveStorage();
      if (!storage) {
        throw new Error("OIDC browser storage is not available");
      }
      const settings: UserManagerSettings = {
        authority: config.authority,
        client_id: clientId,
        redirect_uri: getCallbackUrl(),
        response_type: "code",
        scope: config.scope,
        automaticSilentRenew: false,
        monitorSession: false,
        loadUserInfo: false,
        fetchRequestCredentials: "include",
        stateStore: new WebStorageStateStore({
          prefix: `${config.stateStorePrefix}${clientId}.`,
          store: storage,
        }),
        userStore,
      };
      return new UserManager(settings);
    })();
    try {
      return await userManagerPromise;
    } catch (error) {
      userManagerPromise = null;
      throw error;
    }
  }

  async function startAuthorization(
    redirectPath: string,
    options: StartOidcAuthorizationOptions = {},
  ) {
    if (typeof window === "undefined") {
      return;
    }
    assertOidcBrowserSecureContext();
    const nextRedirectPath = sanitizeRedirectPath(redirectPath, config.defaultRedirectPath);
    setStoredPendingRedirectPath(nextRedirectPath);
    const userManager = await getUserManager();
    const signinArgs: Parameters<UserManager["signinRedirect"]>[0] = {
      state: {
        redirect_path: nextRedirectPath,
      },
    };
    if (options.prompt) {
      signinArgs.prompt = options.prompt;
    }
    await userManager.signinRedirect(signinArgs);
  }

  async function completeCallback(
    fetchImpl: typeof fetch = fetch,
    currentUrl?: string,
  ): Promise<CompleteOidcCallbackResult> {
    const callbackUrl = currentUrl || (typeof window !== "undefined" ? window.location.href : "");
    async function run(forceRefresh = false): Promise<User> {
      const userManager = await getUserManager(fetchImpl, forceRefresh);
      try {
        return await userManager.signinRedirectCallback(callbackUrl);
      } catch (error) {
        if (!forceRefresh && isInvalidClientError(error)) {
          reset();
          return await run(true);
        }
        throw error;
      }
    }

    const oidcUser = await run(false);
    const redirectPath = sanitizeRedirectPath(
      String(
        (oidcUser.state as { redirect_path?: string } | null)?.redirect_path ||
          getStoredPendingRedirectPath() ||
          config.defaultRedirectPath,
      ),
      config.defaultRedirectPath,
    );
    setStoredPendingRedirectPath(null);
    return {
      redirectPath,
      oidcUser,
      restorePendingRedirectPath: () => setStoredPendingRedirectPath(redirectPath),
    };
  }

  function consumePendingRedirectPath(defaultPath = config.defaultRedirectPath) {
    const redirectPath = sanitizeRedirectPath(
      getStoredPendingRedirectPath() || defaultPath,
      defaultPath,
    );
    setStoredPendingRedirectPath(null);
    return redirectPath;
  }

  return {
    completeCallback,
    consumePendingRedirectPath,
    getCallbackUrl,
    getStoredPendingRedirectPath,
    reset,
    setStoredPendingRedirectPath,
    startAuthorization,
  };
}
